import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { apiUrl } from "../auth/config";
import { useAuth } from "../auth/AuthContext";
import "../styles/app.css";
import "../styles/detail.css";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "retained", label: "Retained" },
  { value: "closed_lost", label: "Closed lost" },
];

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Local-part of email for compact list labels. */
function emailLocalPart(address) {
  if (!address) return "rep";
  const at = String(address).indexOf("@");
  return at > 0 ? String(address).slice(0, at) : String(address);
}

function sourceLabel(source) {
  if (source === "schedule-consultation") return "Schedule consultation";
  if (source === "contact-form") return "Contact form";
  return source || "—";
}

function formatScheduleTime(hm) {
  if (!hm) return "—";
  const [hRaw, m] = hm.split(":");
  let h = Number(hRaw);
  if (Number.isNaN(h)) return hm;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${suffix}`;
}

function formatScheduleDate(ymd) {
  if (!ymd) return "—";
  try {
    const [y, mo, d] = ymd.split("-").map(Number);
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(y, mo - 1, d));
  } catch {
    return ymd;
  }
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const { user, loading, isAuthenticated, isAdmin, logout, getIdToken } = useAuth();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("new");
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailConfirm, setEmailConfirm] = useState(false);
  const [error, setError] = useState("");
  const [pendingApproval, setPendingApproval] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [postingNote, setPostingNote] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [expandedMessageId, setExpandedMessageId] = useState(null);

  const authedFetch = useCallback(
    async (path, options = {}) => {
      const token = await getIdToken();
      const res = await fetch(`${apiUrl}${path}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          ...(options.headers || {}),
        },
      });
      return res;
    },
    [getIdToken]
  );

  const load = useCallback(async () => {
    if (!leadId) return;
    setFetching(true);
    setError("");
    setPendingApproval(false);
    try {
      const [leadRes, notesRes, messagesRes] = await Promise.all([
        authedFetch(`/leads/${encodeURIComponent(leadId)}`),
        authedFetch(`/leads/${encodeURIComponent(leadId)}/notes`),
        authedFetch(`/leads/${encodeURIComponent(leadId)}/messages`),
      ]);

      if (
        leadRes.status === 403 ||
        notesRes.status === 403 ||
        messagesRes.status === 403
      ) {
        setPendingApproval(true);
        return;
      }
      if (!leadRes.ok) {
        throw new Error(
          leadRes.status === 404 ? "Lead not found" : `Could not load lead (${leadRes.status})`
        );
      }
      const leadData = await leadRes.json();
      setLead(leadData.lead);
      setStatus(leadData.lead?.status || "new");
      setSchedDate(leadData.lead?.scheduledDate || "");
      setSchedTime(leadData.lead?.scheduledTime || "");

      if (notesRes.ok) {
        const notesData = await notesRes.json();
        setNotes(Array.isArray(notesData.notes) ? notesData.notes : []);
      } else {
        setNotes([]);
      }

      if (messagesRes.ok) {
        const msgData = await messagesRes.json();
        setMessages(Array.isArray(msgData.messages) ? msgData.messages : []);
      } else {
        setMessages([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load lead");
      setLead(null);
    } finally {
      setFetching(false);
    }
  }, [authedFetch, leadId]);

  useEffect(() => {
    if (!isAuthenticated) return;
    load();
  }, [isAuthenticated, load]);

  async function saveStatus(e) {
    e.preventDefault();
    if (!leadId) return;
    setSaving(true);
    setError("");
    try {
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`Could not update status (${res.status})`);
      const data = await res.json();
      setLead(data.lead);
      setStatus(data.lead.status || status);
    } catch (err) {
      setError(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function assignToMe() {
    if (!leadId) return;
    setSaving(true);
    setError("");
    try {
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        body: JSON.stringify({ assignToMe: true }),
      });
      if (!res.ok) throw new Error(`Could not assign (${res.status})`);
      const data = await res.json();
      setLead(data.lead);
    } catch (err) {
      setError(err.message || "Assign failed");
    } finally {
      setSaving(false);
    }
  }

  async function unassign() {
    if (!leadId) return;
    if (!window.confirm("Unassign this lead?")) return;
    setSaving(true);
    setError("");
    try {
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        body: JSON.stringify({ clearAssignee: true }),
      });
      if (!res.ok) throw new Error(`Could not unassign (${res.status})`);
      const data = await res.json();
      setLead(data.lead);
    } catch (err) {
      setError(err.message || "Unassign failed");
    } finally {
      setSaving(false);
    }
  }

  function startEditName() {
    setEditFirstName(lead?.firstName || "");
    setEditLastName(lead?.lastName || "");
    setEditingName(true);
    setError("");
  }

  function cancelEditName() {
    setEditingName(false);
    setEditFirstName("");
    setEditLastName("");
  }

  async function saveName(e) {
    e.preventDefault();
    if (!leadId) return;
    const firstName = editFirstName.trim();
    const lastName = editLastName.trim();
    if (!firstName || !lastName) {
      setError("First and last name are required.");
      return;
    }
    setSavingName(true);
    setError("");
    try {
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        body: JSON.stringify({ firstName, lastName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Could not update name (${res.status})`);
      }
      const data = await res.json();
      setLead(data.lead);
      setEditingName(false);
    } catch (err) {
      setError(err.message || "Name update failed");
    } finally {
      setSavingName(false);
    }
  }

  async function saveSchedule(e) {
    e.preventDefault();
    if (!leadId) return;
    setSavingSchedule(true);
    setError("");
    try {
      // HTML time inputs can omit leading zero; API expects HH:MM.
      const normalizedTime = schedTime.replace(/^(\d):/, "0$1");
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        body: JSON.stringify({
          scheduledDate: schedDate,
          scheduledTime: normalizedTime,
          timezone: "America/New_York",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Could not save schedule (${res.status})`);
      }
      const data = await res.json();
      setLead(data.lead);
      setSchedDate(data.lead.scheduledDate || "");
      setSchedTime(data.lead.scheduledTime || "");
    } catch (err) {
      setError(err.message || "Schedule save failed");
    } finally {
      setSavingSchedule(false);
    }
  }

  async function clearSchedule() {
    if (!leadId) return;
    if (!window.confirm("Clear the scheduled consultation on this lead?")) return;
    setSavingSchedule(true);
    setError("");
    try {
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        body: JSON.stringify({ clearSchedule: true }),
      });
      if (!res.ok) throw new Error(`Could not clear schedule (${res.status})`);
      const data = await res.json();
      setLead(data.lead);
      setSchedDate("");
      setSchedTime("");
    } catch (err) {
      setError(err.message || "Clear failed");
    } finally {
      setSavingSchedule(false);
    }
  }

  async function addNote(e) {
    e.preventDefault();
    if (!leadId || !noteDraft.trim()) return;
    setPostingNote(true);
    setError("");
    try {
      const res = await authedFetch(`/leads/${encodeURIComponent(leadId)}/notes`, {
        method: "POST",
        body: JSON.stringify({ body: noteDraft.trim() }),
      });
      if (!res.ok) throw new Error(`Could not add note (${res.status})`);
      const data = await res.json();
      setNotes((prev) => [...prev, data.note]);
      setNoteDraft("");
      if (data.assignee) {
        setLead((prev) =>
          prev
            ? {
                ...prev,
                assigneeUserId: data.assignee.assigneeUserId,
                assigneeEmail: data.assignee.assigneeEmail,
              }
            : prev
        );
      }
    } catch (err) {
      setError(err.message || "Note failed");
    } finally {
      setPostingNote(false);
    }
  }

  async function sendEmail(e) {
    e.preventDefault();
    if (!leadId || !lead?.email) return;
    if (!emailConfirm) {
      setError("Confirm the recipient matches this lead before sending.");
      return;
    }
    if (
      !window.confirm(
        `Send email to ${lead.email}?\n\nSubject: ${emailSubject.trim()}`
      )
    ) {
      return;
    }
    setSendingEmail(true);
    setError("");
    try {
      const res = await authedFetch(
        `/leads/${encodeURIComponent(leadId)}/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            to: lead.email,
            subject: emailSubject.trim(),
            bodyText: emailBody.trim(),
            confirmRecipient: true,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Could not send email (${res.status})`);
      }
      setMessages((prev) => [...prev, data.message]);
      setEmailSubject("");
      setEmailBody("");
      setEmailConfirm(false);
      if (data.leadTouched) {
        setLead((prev) =>
          prev
            ? {
                ...prev,
                lastContactedAt: data.leadTouched.lastContactedAt,
                status: data.leadTouched.status || prev.status,
                assigneeUserId:
                  data.leadTouched.assigneeUserId || prev.assigneeUserId,
                assigneeEmail:
                  data.leadTouched.assigneeEmail || prev.assigneeEmail,
              }
            : prev
        );
        if (data.leadTouched.status) setStatus(data.leadTouched.status);
      }
    } catch (err) {
      setError(err.message || "Send failed");
    } finally {
      setSendingEmail(false);
    }
  }

  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <p className="leads-support">Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link className="app-header__brand" to="/">
          FTOJ
        </Link>
        <div className="app-header__meta">
          <Link to="/leads">Inbox</Link>
          {isAdmin ? <Link to="/proofs">Proofs</Link> : null}
          <Link to="/security">Security</Link>
          <span>Jones &amp; Booth</span>
          <span>{user?.signInDetails?.loginId || user?.username}</span>
          <button className="btn btn--ghost" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main">
        <p className="detail-back">
          <Link to="/leads">← Lead inbox</Link>
        </p>

        {fetching ? <p className="leads-support">Loading lead…</p> : null}

        {pendingApproval ? (
          <section className="leads-panel">
            <h2>Pending admin approval</h2>
            <p>Lead access requires admin confirmation.</p>
          </section>
        ) : null}

        {error ? <p className="auth-error">{error}</p> : null}

        {lead ? (
          <>
            <div className="leads-heading">
              <div>
                {editingName ? (
                  <form className="name-edit-form" onSubmit={saveName}>
                    <label>
                      <span className="sr-only">First name</span>
                      <input
                        type="text"
                        value={editFirstName}
                        onChange={(e) => setEditFirstName(e.target.value)}
                        placeholder="First name"
                        required
                        autoFocus
                      />
                    </label>
                    <label>
                      <span className="sr-only">Last name</span>
                      <input
                        type="text"
                        value={editLastName}
                        onChange={(e) => setEditLastName(e.target.value)}
                        placeholder="Last name"
                        required
                      />
                    </label>
                    <div className="name-edit-actions">
                      <button
                        className="btn btn--primary"
                        type="submit"
                        disabled={savingName}
                      >
                        {savingName ? "Saving…" : "Save name"}
                      </button>
                      <button
                        className="btn btn--ghost"
                        type="button"
                        onClick={cancelEditName}
                        disabled={savingName}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="leads-title-row">
                      <h1 className="leads-title">
                        {lead.firstName} {lead.lastName}
                      </h1>
                      <button
                        className="btn btn--ghost btn--compact"
                        type="button"
                        onClick={startEditName}
                      >
                        Edit name
                      </button>
                    </div>
                    <p className="leads-support">
                      {lead.caseType || "Case TBD"} · {sourceLabel(lead.source)} ·{" "}
                      {formatWhen(lead.createdAt)}
                    </p>
                  </>
                )}
              </div>
              <button className="btn btn--ghost" type="button" onClick={load} disabled={fetching}>
                Refresh
              </button>
            </div>

            <div className="detail-grid">
              <section className="detail-card">
                <h2>Contact</h2>
                <dl className="detail-dl">
                  <div>
                    <dt>Email</dt>
                    <dd>
                      <a href={`mailto:${lead.email}`}>{lead.email || "—"}</a>
                    </dd>
                  </div>
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      <a href={`tel:${lead.phone}`}>{lead.phone || "—"}</a>
                    </dd>
                  </div>
                  <div>
                    <dt>Referral</dt>
                    <dd>{lead.referralLabel || lead.referralCode || "—"}</dd>
                  </div>
                  <div>
                    <dt>Assignee</dt>
                    <dd>{lead.assigneeEmail || "Unassigned"}</dd>
                  </div>
                </dl>
                <button
                  className="btn btn--ghost"
                  type="button"
                  onClick={assignToMe}
                  disabled={saving}
                >
                  Assign to me
                </button>
                {lead.assigneeUserId || lead.assigneeEmail ? (
                  <button
                    className="btn btn--ghost"
                    type="button"
                    onClick={unassign}
                    disabled={saving}
                    style={{ marginLeft: "0.5rem" }}
                  >
                    Unassign
                  </button>
                ) : null}
              </section>

              <section className="detail-card">
                <h2>Workflow</h2>
                <form className="detail-status" onSubmit={saveStatus}>
                  <label>
                    Status
                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                      {STATUSES.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button className="btn btn--primary" type="submit" disabled={saving}>
                    {saving ? "Saving…" : "Save status"}
                  </button>
                </form>
                {lead.lastContactedAt ? (
                  <p className="detail-muted">Last contacted {formatWhen(lead.lastContactedAt)}</p>
                ) : null}
              </section>

              <section className="detail-card detail-card--wide">
                <h2>Scheduled consultation</h2>
                <p className="detail-muted detail-muted--flush">
                  Client may have picked a time on jonesboothlaw.com, or you can
                  record / confirm when they opted to be called after contact.
                </p>

                {lead.scheduledDate && lead.scheduledTime ? (
                  <div className="schedule-current">
                    <p className="schedule-when">
                      {formatScheduleDate(lead.scheduledDate)} ·{" "}
                      {formatScheduleTime(lead.scheduledTime)}{" "}
                      <span className="schedule-tz">
                        {lead.timezone === "America/New_York" || !lead.timezone
                          ? "ET"
                          : lead.timezone}
                      </span>
                    </p>
                    <p className="detail-muted">
                      {lead.scheduleSetBy === "crm"
                        ? `Recorded in CRM${
                            lead.scheduledUpdatedByEmail
                              ? ` by ${lead.scheduledUpdatedByEmail}`
                              : ""
                          }${
                            lead.scheduledUpdatedAt
                              ? ` · ${formatWhen(lead.scheduledUpdatedAt)}`
                              : ""
                          }`
                        : lead.source === "schedule-consultation"
                          ? "Requested by client on the law firm site"
                          : "On file"}
                    </p>
                  </div>
                ) : (
                  <p className="detail-muted">No consultation time on file yet.</p>
                )}

                <form className="schedule-form" onSubmit={saveSchedule}>
                  <label>
                    Date
                    <input
                      type="date"
                      value={schedDate}
                      onChange={(e) => setSchedDate(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Time (ET)
                    <input
                      type="time"
                      value={schedTime}
                      onChange={(e) => setSchedTime(e.target.value)}
                      required
                    />
                  </label>
                  <div className="schedule-actions">
                    <button
                      className="btn btn--primary"
                      type="submit"
                      disabled={savingSchedule || !schedDate || !schedTime}
                    >
                      {savingSchedule
                        ? "Saving…"
                        : lead.scheduledDate
                          ? "Update / confirm schedule"
                          : "Set scheduled consult"}
                    </button>
                    {lead.scheduledDate ? (
                      <button
                        className="btn btn--ghost"
                        type="button"
                        onClick={clearSchedule}
                        disabled={savingSchedule}
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                </form>
              </section>

              <section className="detail-card">
                <h2>Consents</h2>
                <ul className="detail-consents">
                  <li className={lead.consentSms ? "ok" : ""}>
                    SMS {lead.consentSms ? "yes" : "no"}
                  </li>
                  <li className={lead.agreeTerms ? "ok" : ""}>
                    Terms {lead.agreeTerms ? "yes" : "no"}
                  </li>
                  <li className={lead.agreePrivacy ? "ok" : ""}>
                    Privacy {lead.agreePrivacy ? "yes" : "no"}
                  </li>
                </ul>
                <p className="detail-muted">
                  Consented {lead.consentedAt ? formatWhen(lead.consentedAt) : "—"}
                </p>
              </section>

              <section className="detail-card detail-card--wide">
                <h2>Case notes from intake</h2>
                <p className="detail-desc">{lead.description || "No description captured."}</p>
              </section>

              <section className="detail-card detail-card--wide">
                <h2>Email</h2>
                <p className="detail-muted detail-muted--flush">
                  From Jones &amp; Booth Law &lt;admin@jonesboothlaw.com&gt;.
                  Client replies go to your inbox (Reply-To) — they are{" "}
                  <strong>not</strong> pulled into this feed yet.
                </p>

                <ul className="messages-list">
                  {messages.length === 0 ? (
                    <li className="detail-muted">No emails sent yet.</li>
                  ) : (
                    [...messages]
                      .sort((a, b) => {
                        const ta = a.createdAt || "";
                        const tb = b.createdAt || "";
                        return tb.localeCompare(ta);
                      })
                      .map((m) => {
                        const open = expandedMessageId === m.messageId;
                        const who = emailLocalPart(m.sentByEmail || m.from);
                        return (
                          <li key={m.messageId} className="message-item">
                            <button
                              type="button"
                              className={`message-toggle${open ? " is-open" : ""}`}
                              aria-expanded={open}
                              onClick={() =>
                                setExpandedMessageId(open ? null : m.messageId)
                              }
                            >
                              <span className="message-toggle__subject">
                                {m.subject || "(no subject)"}
                              </span>
                              <span className="message-toggle__meta">
                                <span className="message-toggle__who">{who}</span>
                                <span className="message-toggle__when">
                                  {formatWhen(m.createdAt)}
                                </span>
                              </span>
                            </button>
                            {open ? (
                              <div className="message-panel">
                                <p className="message-body">{m.bodyText}</p>
                                <p className="notes-meta">
                                  To {m.to}
                                  {m.direction ? ` · ${m.direction}` : ""}
                                  {m.providerId ? ` · SES ${m.providerId}` : ""}
                                </p>
                              </div>
                            ) : null}
                          </li>
                        );
                      })
                  )}
                </ul>

                {lead.email ? (
                  <form className="email-form" onSubmit={sendEmail}>
                    <label>
                      To
                      <input type="email" value={lead.email} readOnly disabled />
                    </label>
                    <label>
                      Subject
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Subject"
                        maxLength={200}
                        required
                      />
                    </label>
                    <label>
                      Message
                      <textarea
                        rows={5}
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        placeholder="Write your message…"
                        maxLength={10000}
                        required
                      />
                    </label>
                    <label className="email-confirm">
                      <input
                        type="checkbox"
                        checked={emailConfirm}
                        onChange={(e) => setEmailConfirm(e.target.checked)}
                      />
                      <span>
                        I confirm this email goes to{" "}
                        <strong>{lead.email}</strong> (the lead on this record).
                      </span>
                    </label>
                    <button
                      className="btn btn--primary"
                      type="submit"
                      disabled={
                        sendingEmail ||
                        !emailConfirm ||
                        !emailSubject.trim() ||
                        !emailBody.trim()
                      }
                    >
                      {sendingEmail ? "Sending…" : "Send email"}
                    </button>
                  </form>
                ) : (
                  <p className="detail-muted">
                    This lead has no email on file — cannot send.
                  </p>
                )}
              </section>

              <section className="detail-card detail-card--wide">
                <h2>CRM notes</h2>
                <ul className="notes-list">
                  {notes.length === 0 ? (
                    <li className="detail-muted">No notes yet.</li>
                  ) : (
                    notes.map((n) => (
                      <li key={n.noteId}>
                        <p className="notes-body">{n.body}</p>
                        <p className="notes-meta">
                          {n.authorEmail || "rep"} · {formatWhen(n.createdAt)}
                        </p>
                      </li>
                    ))
                  )}
                </ul>
                <form className="notes-form" onSubmit={addNote}>
                  <label>
                    <span className="sr-only">Add note</span>
                    <textarea
                      rows={3}
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Add a note…"
                      required
                    />
                  </label>
                  <button
                    className="btn btn--primary"
                    type="submit"
                    disabled={postingNote || !noteDraft.trim()}
                  >
                    {postingNote ? "Posting…" : "Add note"}
                  </button>
                </form>
              </section>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
